import {
  AmericanFinalBracketSize,
  AmericanGroupFormat,
  OverflowGroupClassification,
  TournamentFormat,
} from '@/types/tournament.model';

/** Returns true when the tournament format is Americano. */
export function isAmericanFormat(
  format: TournamentFormat | undefined | null,
): boolean {
  return format === TournamentFormat.AMERICAN;
}

/** Whether a new tournament counts for the category ranking by default: off for Americano. */
export function defaultCountsForCategoryRanking(
  format: TournamentFormat | undefined | null,
): boolean {
  return !isAmericanFormat(format);
}

/** Spanish labels for API `format` (SHORT | AMERICAN | LONG). */
export function getTournamentFormatLabel(format: TournamentFormat): string {
  switch (format) {
    case TournamentFormat.SHORT:
      return 'Corto';
    case TournamentFormat.AMERICAN:
      return 'Americano';
    case TournamentFormat.LONG:
      return 'Largo';
    default:
      return format;
  }
}

export const HEXAGONAL_TEAM_COUNT = 6;

/** Spanish label for the AMERICAN final bracket; "Hexagonal" once a 6-team NONE fixture exists. */
export function getAmericanFinalBracketLabel(
  size: AmericanFinalBracketSize | undefined,
  options?: { teamCount?: number; hasFixture?: boolean },
): string {
  if (
    size === AmericanFinalBracketSize.NONE &&
    options?.hasFixture &&
    options.teamCount === HEXAGONAL_TEAM_COUNT
  ) {
    return 'Hexagonal (un solo grupo de 6, todos contra todos)';
  }
  switch (size) {
    case AmericanFinalBracketSize.FINAL:
      return 'Solo final (1° de cada grupo)';
    case AmericanFinalBracketSize.FOUR:
      return 'Cuadro de 4';
    case AmericanFinalBracketSize.EIGHT:
      return 'Cuadro de 8';
    case AmericanFinalBracketSize.NONE:
    default:
      return 'Sin cuadro final (solo fase de grupos)';
  }
}

/** Campos mínimos que codifican el formato de partidos del americano. */
export interface AmericanGroupFormatSource {
  americanGroupFormat?: AmericanGroupFormat;
  overflowGroupClassification?: OverflowGroupClassification;
}

/**
 * Resolves the effective AMERICAN in-group match format.
 * Padel stores it in `americanGroupFormat`; squash/tennis reuse
 * `overflowGroupClassification` (EXTRA_CLASSIFIER = "por resultado").
 * Acepta tanto un {@link Tournament} como el slice del wizard.
 */
export function resolveAmericanGroupFormat(
  config: AmericanGroupFormatSource,
): AmericanGroupFormat {
  if (config.americanGroupFormat !== undefined) {
    return config.americanGroupFormat;
  }
  return config.overflowGroupClassification ===
    OverflowGroupClassification.EXTRA_CLASSIFIER
    ? AmericanGroupFormat.WINNER_LOSER
    : AmericanGroupFormat.ROUND_ROBIN;
}

/** Spanish label for the AMERICAN in-group match format. */
export function getAmericanGroupFormatLabel(
  config: AmericanGroupFormatSource,
): string {
  return resolveAmericanGroupFormat(config) === AmericanGroupFormat.WINNER_LOSER
    ? 'Por resultado: ganador vs ganador / perdedor vs perdedor.'
    : 'Todos contra todos (round-robin).';
}
